import {
  AxiosAPIError,
  BadgeEventEntity,
  badgesEventsKeys,
  FanAccessRequest,
  FanActivityEntity,
  FanBadgeLinkBody,
  FanEntity,
  FanListEntity,
  fansKeys,
  FanStatsEntity,
  ListEntity,
  ListEntityV2,
  PaginationQuery,
  SortDirectionMap,
} from '@seaters-app/constants';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  fetchFanAccessRequests,
  fetchFanActivities,
  fetchFanGroupFans,
  fetchFanPersonalInfo,
  fetchFanPositions,
  fetchFanStats,
  grantUserBadge,
  revokeUserBadge,
} from '../api';
import { TablePaginationConfig } from 'antd';
import { useState } from 'react';
import { SorterResult } from 'antd/es/table/interface';

export const useFetchFanGroupFans = (
  fanGroupId: string,
  params: PaginationQuery
) =>
  useQuery<ListEntity<FanListEntity>, Error>(
    fansKeys.listPerFG(fanGroupId, params),
    () => fetchFanGroupFans(fanGroupId, params),
    {
      enabled: !!fanGroupId,
      keepPreviousData: true,
    }
  );

enum FanPositionsSortFieldMap {
  'waitingListStatusDate' = 'lastModifiedDate',
}

export const useFetchFanPositions = (fanId: string, groupId: string) => {
  const [paginationParams, setPaginationParams] = useState<{
    pageSize: number | undefined;
    current: number | undefined;
    sortField?: SorterResult<any>['field'];
    sortOrder?: SorterResult<any>['order'];
  }>({
    pageSize: 10,
    current: 1,
    sortField: 'waitingListStatusDate',
    sortOrder: 'ascend',
  });

  const params = {
    page: paginationParams.current ? paginationParams.current - 1 : undefined,
    size: paginationParams.pageSize,
    sort:
      paginationParams.sortField &&
      !Array.isArray(paginationParams.sortField) &&
      paginationParams.sortOrder
        ? `${
            FanPositionsSortFieldMap[
              paginationParams.sortField as keyof typeof FanPositionsSortFieldMap
            ]
          }:${SortDirectionMap[paginationParams.sortOrder]}`
        : undefined,
  };

  const { data, isLoading } = useQuery<ListEntityV2<FanActivityEntity>, Error>(
    fansKeys.positionsPerFan(fanId, groupId, params),
    () => fetchFanPositions(fanId, groupId, params),
    {
      enabled: !!fanId && !!groupId,
      keepPreviousData: true,
    }
  );

  const handleTableChange = (
    { pageSize, current }: TablePaginationConfig,
    filters: any,
    sorter: any
  ) => {
    setPaginationParams({
      pageSize,
      current: current,
      sortField: sorter.field,
      sortOrder: sorter.order,
    });
  };

  const resetPage = () => {
    setPaginationParams({
      ...paginationParams,
      current: 1,
    });
  };

  return {
    activities: data,
    isLoading,
    handleTableChange,
    resetPage,
    pagination: {
      pageSize: paginationParams.pageSize,
      current: paginationParams.current,
      total: data?.totalElements,
      showSizeChanger: true,
    },
  };
};

enum FanActivitiesSortFieldMap {
  'waitingListStatusDate' = 'eventDate',
}

export const useFetchFanActivities = (
  fanId: string,
  groupId: string,
  showClosed: boolean
) => {
  const [paginationParams, setPaginationParams] = useState<{
    pageSize: number | undefined;
    current: number | undefined;
    sortField?: SorterResult<any>['field'];
    sortOrder?: SorterResult<any>['order'];
  }>({
    pageSize: 10,
    current: 1,
    sortField: 'waitingListStatusDate',
    sortOrder: 'ascend',
  });

  const params = {
    page: paginationParams.current ? paginationParams.current - 1 : undefined,
    size: paginationParams.pageSize,
    sort:
      paginationParams.sortField &&
      !Array.isArray(paginationParams.sortField) &&
      paginationParams.sortOrder
        ? `${
            FanActivitiesSortFieldMap[
              paginationParams.sortField as keyof typeof FanActivitiesSortFieldMap
            ]
          }:${SortDirectionMap[paginationParams.sortOrder]}`
        : undefined,
  };

  const { data, isLoading } = useQuery<ListEntityV2<FanActivityEntity>, Error>(
    fansKeys.activitiesPerFan(fanId, groupId, showClosed, params),
    () => fetchFanActivities(fanId, groupId, showClosed, params),
    {
      enabled: !!fanId && !!groupId,
      keepPreviousData: true,
    }
  );

  const handleTableChange = (
    { pageSize, current }: TablePaginationConfig,
    filters: any,
    sorter: any
  ) => {
    setPaginationParams({
      pageSize,
      current: current,
      sortField: sorter.field,
      sortOrder: sorter.order,
    });
  };

  const resetPage = () => {
    setPaginationParams({
      ...paginationParams,
      current: 1,
    });
  };

  return {
    activities: data,
    isLoading,
    handleTableChange,
    resetPage,
    pagination: {
      pageSize: paginationParams.pageSize,
      current: paginationParams.current,
      total: data?.totalElements,
      showSizeChanger: true,
    },
  };
};

export const useFetchFanStats = (fanId: string, groupId: string) =>
  useQuery<FanStatsEntity, Error>(
    fansKeys.statsPerFan(fanId, groupId),
    () => fetchFanStats(fanId, groupId),
    {
      enabled: !!fanId && !!groupId,
      keepPreviousData: true,
    }
  );

export const useFetchFanAccessRequests = (fanId: string, groupId: string) =>
  useQuery<FanAccessRequest[], Error>(
    fansKeys.accessRequestsPerFan(fanId),
    () => fetchFanAccessRequests(fanId, groupId),
    {
      enabled: !!fanId && !!groupId,
      keepPreviousData: true,
    }
  );

export const useFetchFanPersonalInfo = (fanId: string) =>
  useQuery<FanEntity, Error>(
    fansKeys.detail(fanId),
    () => fetchFanPersonalInfo(fanId),
    {
      enabled: !!fanId,
    }
  );

export const useGrantUserBadge = (
  groupId: string
): UseMutationResult<
  BadgeEventEntity,
  AxiosAPIError<FanBadgeLinkBody>,
  FanBadgeLinkBody,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    (body: FanBadgeLinkBody) => grantUserBadge(groupId, body),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(
          badgesEventsKeys.listPerUser(groupId, response.userId)
        );
      },
    }
  );
};
// badgesEventsKeys.listPerUser(fanGroupId, userId),
export const useRevokeUserBadge = (
  groupId: string
): UseMutationResult<
  BadgeEventEntity,
  AxiosAPIError<FanBadgeLinkBody>,
  FanBadgeLinkBody,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(
    (body: FanBadgeLinkBody) => revokeUserBadge(groupId, body),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(
          badgesEventsKeys.listPerUser(groupId, response.userId)
        );
      },
    }
  );
};
